import moment from "moment";
import { TableCellType } from "../../../../components/custom-table/table";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { TableHeader } from "../../../../data-model/customTableModel";
import {
  MedicalPaOrder,
  Status,
} from "../../../../data-model/medicalPaOrdersModel";
import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import { MedicalPaOrdersAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import {
  MedicalOrderReviewStatus,
  MedicalOrderStatus,
} from "../../../../enums/orderReviewStatus";
import { isExceptionFlowForUrologyOrg } from "../../../../enums/organizationTypes";
import { GenericAuthStatusTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { generateDataForDocWithSideNavModalFromSingleMedicalPaOrder } from "../../../../utils/createDataForDocWithSideNavModal";
import { formatDateToUTCWithFormat } from "../../../../utils/formatDate";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { getAuthStatusForTables } from "../../../../utils/getAuthStatusOfMedicalPaOrder";
import { rotateLastSegmentFirst } from "../../../../utils/getFullNameOfTheProvider";
import { getScreenshotValues } from "../../../../utils/getScreenshotValues";
import { capitalizeString } from "../../../../utils/stringModifications";
import { findLabelFromGivenState } from "../../../pharmaPaForm/utils/stateOptions";
import { getOrderIcons } from "../../medicalPaOrdersAlgolia/utils/getOrderIcons";
import { getDefaultAuthStatusTableHeaders } from "./defaultAuthStatusTableHeaders";
import { getHoldTableHeaders } from "./holdTableHeaders";
import { getPendingTableHeaders } from "./pendingTableHeaders";
import { getQueryAuthRequiredTableHeaders } from "./queryAuthRequiredTableHeaders";
import { getSunstateNotToWorkAppointmentsTableHeaders } from "./sunstateNotToWorkAppointmentsTableHeaders";
import { getSunstateOrdersToProcessTableHeaders } from "./sunstateOrdersToProcessTableHeaders";

/** Replaces the Medication column header with an Appointment Type column in the same slot. */
const replaceMedicationWithAppointmentType = (
  header: TableHeader,
): TableHeader =>
  header.key === GenericAuthStatusTableColumnKeys.MEDICATION
    ? {
        label: "Appointment Type",
        key: GenericAuthStatusTableColumnKeys.APPOINTMENT_TYPE,
        order: header.order,
        width: header.width,
        type: TableCellType.STRING,
      }
    : header;

/** Replaces the BO Value column header with an Estimate Value column in the same slot. */
const replaceBoValueWithEstimateValue = (header: TableHeader): TableHeader =>
  header.key === GenericAuthStatusTableColumnKeys.BO_VALUE
    ? {
        label: "Estimate Value",
        key: GenericAuthStatusTableColumnKeys.ESTIMATE_VALUE,
        order: header.order,
        width: header.width,
        type: TableCellType.BADGE,
      }
    : header;

/**
 * Applies urology exception-flow header overrides (Medication → Appointment Type,
 * BO Value → Estimate Value) to a header list.
 *
 * @param headers - The base table headers to transform
 * @returns Headers with exception-flow column relabels applied
 */
const applySunstateHeaderOverrides = (headers: TableHeader[]): TableHeader[] =>
  headers
    .map(replaceMedicationWithAppointmentType)
    .map(replaceBoValueWithEstimateValue);

/** Internal tabId for the "Not To Work - Appointments" worklist (underscored form). */
const NOT_TO_WORK_APPOINTMENTS_TAB_ID = "not_to_work_appointments";

/** Internal tabId for the "Orders to Process" worklist (underscored form). */
const ORDERS_TO_PROCESS_TAB_ID = "orders_to_process";

/**
 * Returns the appropriate table headers based on the tab ID and org ID.
 *
 * @description
 * This function serves as a factory that returns the correct table header
 * configuration based on the current tab selection and organization. Each
 * tab has its own specific column requirements and widths; org-specific
 * overrides (e.g. exception-flow org "Not To Work - Appointments" tab) take
 * precedence over the generic per-tab configuration.
 *
 * Dispatch order:
 * 1. Exception-flow org + `not_to_work_appointments` → Appointment Notes /
 *    Location layout (see `sunstateNotToWorkAppointmentsTableHeaders`).
 * 2. Exception-flow org + `orders_to_process` → default layout with extra
 *    Date of Creation + Location columns (see
 *    `sunstateOrdersToProcessTableHeaders`).
 * 3. Otherwise, fall back to the per-tab layouts (`pending`, `hold`,
 *    `query`/`auth_required`, or default).
 *
 * For every exception-flow org path, `applySunstateHeaderOverrides` relabels the
 * Medication column to "Appointment Type" and the BO Value column to
 * "Estimate Value".
 *
 * @param {string} tabID - The identifier for the current tab (underscored
 *   form, e.g. `not_to_work_appointments`)
 * @param {string} [orgId] - Optional organization ID used to apply
 *   org-specific column overrides
 * @returns {Array<Object>} Array of table header configuration objects
 *
 * @example
 * const headers = getTableHeaders("pending");
 * // Returns pending-specific headers with "Doc Status" column
 *
 * @example
 * const headers = getTableHeaders("hold");
 * // Returns hold-specific headers with "Hold Until" column
 *
 * @example
 * const headers = getTableHeaders(
 *   "not_to_work_appointments",
 *   exceptionFlowForUrologyOrgs[0],
 * );
 * // Returns the exception-flow Appointment Notes + Location layout
 */
export const getTableHeaders = (tabID: string, orgId?: string) => {
  if (
    isExceptionFlowForUrologyOrg(orgId) &&
    tabID === NOT_TO_WORK_APPOINTMENTS_TAB_ID
  ) {
    return applySunstateHeaderOverrides(
      getSunstateNotToWorkAppointmentsTableHeaders(),
    );
  }
  if (
    isExceptionFlowForUrologyOrg(orgId) &&
    tabID === ORDERS_TO_PROCESS_TAB_ID
  ) {
    return applySunstateHeaderOverrides(
      getSunstateOrdersToProcessTableHeaders(),
    );
  }

  let headers;
  if (tabID === "pending") {
    headers = getPendingTableHeaders();
  } else if (tabID === "hold") {
    headers = getHoldTableHeaders();
  } else if (tabID === "query" || tabID === "auth_required") {
    headers = getQueryAuthRequiredTableHeaders();
  } else {
    headers = getDefaultAuthStatusTableHeaders();
  }

  if (isExceptionFlowForUrologyOrg(orgId)) {
    return applySunstateHeaderOverrides(headers);
  }
  return headers;
};

/**
 * Transforms Firestore MedicalPaOrder documents into table-ready row data for the
 * generic auth status table. Formats patient details, dates, plan info, auth badges,
 * RPA/doc upload statuses, left-border urgency indicators, and order review icons.
 *
 * @param medicalPaOrders - Array of MedicalPaOrder documents from Firestore
 * @param authStatusOptions - Auth status options for badge color/text configuration
 * @returns Array of GenericAuthStatusTableData records ready for CustomTable rendering
 */
export const generateGenericAuthStatusTableData = (
  medicalPaOrders: MedicalPaOrder[],
  authStatusOptions: AuthStatusOptionModel[],
): GenericAuthStatusTableData[] => {
  return medicalPaOrders.map((medicalPaOrder) => {
    const authStatus = getAuthStatusForTables(
      medicalPaOrder,
      authStatusOptions,
    );

    return {
      [GenericAuthStatusTableColumnKeys.PATIENT_DETAILS_WITH_LEFT_BORDER]: {
        mainText: capitalizeString(
          generateFullName(
            medicalPaOrder?.demographics?.first_name ?? "",
            "",
            medicalPaOrder?.demographics?.last_name ?? "",
          ),
        ),
        secondaryText: `MRN : ${medicalPaOrder?.demographics?.patient_id ?? medicalPaOrder?.demographics?.patient_mrn ?? ""}`,
        shouldShowLeftBorder: leftBorderShouldShow(medicalPaOrder),
        leftBorderColor: getleftBorderColorforPatientDetails(medicalPaOrder),
      },
      [GenericAuthStatusTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(
            medicalPaOrder?.demographics?.first_name ?? "",
            "",
            medicalPaOrder?.demographics?.last_name ?? "",
          ),
        ),
        secondaryText: `MRN : ${medicalPaOrder?.demographics?.patient_id ?? medicalPaOrder?.demographics?.patient_mrn ?? ""}`,
      },
      [GenericAuthStatusTableColumnKeys.DATE_OF_SERVICE]:
        formatDateToUTCWithFormat(
          medicalPaOrder?.payload?.date_of_service ?? "",
          "MM/DD/YYYY",
          "--",
        ),
      [GenericAuthStatusTableColumnKeys.PLAN_DETAILS]: {
        mainText: medicalPaOrder?.coverage?.primary?.payer_name ?? "",
        secondaryText: medicalPaOrder?.coverage?.primary?.member_id ?? "",
      },
      [GenericAuthStatusTableColumnKeys.MEDICATION]:
        medicalPaOrder?.payload?.regimen_name ?? "",
      [GenericAuthStatusTableColumnKeys.APPOINTMENT_TYPE]:
        medicalPaOrder?.payload?.regimen_name ?? "",
      [GenericAuthStatusTableColumnKeys.ASSIGNEE]:
        capitalizeString(medicalPaOrder?.assigned_to?.provider_name) ?? "--",
      [GenericAuthStatusTableColumnKeys.STATE]: findLabelFromGivenState(
        medicalPaOrder?.demographics?.address?.state ?? "",
      ),
      [GenericAuthStatusTableColumnKeys.ROW_DATA]:
        JSON.stringify(medicalPaOrder),
      [GenericAuthStatusTableColumnKeys.ID]: medicalPaOrder?.id ?? "",
      [GenericAuthStatusTableColumnKeys.CREATED_AT]: formatDateToUTCWithFormat(
        medicalPaOrder?.created_at ?? "",
        "MM/DD/YYYY",
        "--",
      ),
      [GenericAuthStatusTableColumnKeys.DATE_OF_WORK]:
        medicalPaOrder?.timestamps?.date_of_work ?? null,

      [GenericAuthStatusTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
      },
      [GenericAuthStatusTableColumnKeys.LAST_CHECKED]: medicalPaOrder
        ?.timestamps?.date_of_last_checked
        ? moment(medicalPaOrder?.timestamps?.date_of_last_checked).format(
            "MM/DD/YYYY",
          )
        : "--",
      [GenericAuthStatusTableColumnKeys.HOLD_UNTIL]: medicalPaOrder?.timestamps
        ?.date_of_hold_until
        ? moment(medicalPaOrder?.timestamps?.date_of_hold_until).format(
            "MM/DD/YYYY",
          )
        : "--",

      [GenericAuthStatusTableColumnKeys.BO_VALUE]: {
        text: medicalPaOrder?.status?.bo_status ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [GenericAuthStatusTableColumnKeys.ESTIMATE_VALUE]: {
        text: medicalPaOrder?.status?.estimate_value ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [GenericAuthStatusTableColumnKeys.RPA_STATUS]: {
        status:
          medicalPaOrder?.status?.ev_write_back_status ??
          EVWriteBackStatus.NOT_INITIATED,
        id: medicalPaOrder?.id ?? "",
        shouldPropagate: true,
        masterAuthStatus: medicalPaOrder?.status?.master_auth_status ?? "",
      },
      [GenericAuthStatusTableColumnKeys.RPA_STATUS_TWO]: {
        status:
          medicalPaOrder?.status?.document_upload_status ??
          DocWriteBackStatus.NOT_INITIATED,
        filePaths: getScreenshotValues(
          medicalPaOrder?.auth_on_file?.screenshots
            ?.write_note_cig_box_screenshot,
        ),
        docList:
          generateDataForDocWithSideNavModalFromSingleMedicalPaOrder(
            medicalPaOrder,
          ),
        masterAuthStatus: medicalPaOrder?.status?.master_auth_status ?? "",
        shouldPropagate: true,
      },
      [GenericAuthStatusTableColumnKeys.PROVIDER]: rotateLastSegmentFirst(
        medicalPaOrder?.payload?.practitioner_name ?? "",
      ),
      [GenericAuthStatusTableColumnKeys.SIDE_MODAL_ICON]: {
        id: medicalPaOrder?.id ?? "",
        patientId:
          medicalPaOrder?.demographics?.patient_id ??
          medicalPaOrder?.demographics?.patient_mrn ??
          "",
      },
      [GenericAuthStatusTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS]: {
        ...getOrderIcons(
          (medicalPaOrder?.status as Status)?.medical_order_status as
            MedicalOrderStatus | undefined,
          (medicalPaOrder?.status as Status)?.medical_order_review_status as
            MedicalOrderReviewStatus | undefined,
          medicalPaOrder?.status?.master_auth_status === "query" &&
            medicalPaOrder?.ai_agent_type === "agent",
        ),
        order_id: medicalPaOrder?.id ?? "",
      },
    };
  });
};

/**
 * Determines whether a left border indicator should be shown for a medical PA order.
 * Applies only to orders with "Pending" or "Hold" master auth status.
 *
 * @param order_data - The medical PA order to evaluate
 * @returns True if the order's master auth status is Pending or Hold
 */
export const leftBorderShouldShow = (order_data: MedicalPaOrder) => {
  if (
    order_data?.status?.master_auth_status ===
      MedicalPaOrdersAuthStatus.Pending ||
    order_data?.status?.master_auth_status === MedicalPaOrdersAuthStatus.Hold
  ) {
    return true;
  }
  return false;
};

/**
 * Calculates the urgency-based left border color for a medical PA order row.
 *
 * Color coding by hours since last action:
 * - Orange (#EF6C00): 24-48 hours elapsed
 * - Red (#CD0202): 48+ hours elapsed
 * - Transparent: < 24 hours or not applicable
 *
 * For Pending orders uses date_of_last_checked; for Hold orders uses date_of_hold_until.
 *
 * @param medicalPaOrder - The medical PA order to evaluate
 * @returns Hex color code or "transparent"
 */
export const getleftBorderColorforPatientDetails = (
  medicalPaOrder: MedicalPaOrder,
) => {
  if (
    medicalPaOrder?.timestamps?.date_of_last_checked &&
    medicalPaOrder?.status?.master_auth_status ===
      MedicalPaOrdersAuthStatus.Pending
  ) {
    const lastCheckedTime = moment(
      medicalPaOrder.timestamps.date_of_last_checked,
    );
    const now = moment();
    const hoursSinceLastChecked = now.diff(lastCheckedTime, "hours");

    if (hoursSinceLastChecked >= 24 && hoursSinceLastChecked < 48) {
      return "#EF6C00";
    } else if (hoursSinceLastChecked >= 48) {
      return "#CD0202";
    }
  } else if (
    medicalPaOrder?.timestamps?.date_of_hold_until &&
    medicalPaOrder?.status?.master_auth_status ===
      MedicalPaOrdersAuthStatus.Hold
  ) {
    const lastCheckedTime = moment(
      medicalPaOrder.timestamps.date_of_hold_until,
    );
    const now = moment();
    const hoursSinceLastChecked = now.diff(lastCheckedTime, "hours");

    if (hoursSinceLastChecked >= 24 && hoursSinceLastChecked < 48) {
      return "#EF6C00";
    } else if (hoursSinceLastChecked >= 48) {
      return "#CD0202";
    }
  }
  return "transparent";
};
